/**
 * Tenant config **backups (snapshots)** card.
 *
 * A configurable-ready content component: it renders the integrated `DataList`
 * (server mode → `GET /config/snapshots`) from a declarative, stable-keyed
 * column config, reads the injected `_layoutOverrides` meta-prop, publishes its
 * base config for the admin layout editor, and resolves its title through
 * `resolveCardTitle`. Registered in `../register.ts` as
 * `claimflow.configBackupsCard`.
 *
 * Header exposes **Créer une sauvegarde** (optional label → `POST
 * /config/snapshots`). Each row exposes **Restaurer** → two-step restore: a
 * dry-run (`dry_run: true`) `RestoreReport` preview, then on confirm the real
 * merge-only restore (`dry_run: false`) + success toast.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { Archive, Plus } from 'lucide-react';

import { Button } from '@components/base/actions';
import { Icon, Text } from '@components/base/display';
import { Alert, Modal, Spinner, useToast } from '@components/base/feedback';
import { TextField } from '@components/base/inputs';
import { HStack, VStack } from '@components/base/layout';
import {
  DataList,
  dataListWorkflowRegistry,
  useDataList,
} from '@components/integrated/data-list';
import {
  applyLayoutOverrides,
  buildPublishedConfigFromDataList,
  resolveCardTitle,
  usePublishLayoutConfig,
  type LayoutOverrides,
} from '@components/shared/configurable';

import type { ConfigSnapshot } from '../../api/configVersioningService';
import {
  useCreateSnapshot,
  useRestoreSnapshot,
} from '../../hooks/configVersioning';
import { createConfigBackupsListConfig } from '../../configs/configVersioningListConfigs';
import { RestoreReportView } from './RestoreReportView';
import { restoreReportSummary } from './configVersioningFormat';

const COMPONENT_ID = 'claimflow.configBackupsCard';
const BASE_TITLE = 'Sauvegardes';

export interface ConfigBackupsCardProps {
  /**
   * Per-tenant column layout overrides injected by the `withComponentRules`
   * HOC. `undefined` at baseline ⇒ byte-identical render.
   */
  _layoutOverrides?: LayoutOverrides;
}

export function ConfigBackupsCard({
  _layoutOverrides,
}: Readonly<ConfigBackupsCardProps> = {}) {
  const { toast } = useToast();

  const baseConfig = useMemo(() => createConfigBackupsListConfig(), []);
  usePublishLayoutConfig(
    COMPONENT_ID,
    buildPublishedConfigFromDataList(baseConfig, BASE_TITLE),
  );
  const config = useMemo(
    () => applyLayoutOverrides(baseConfig, _layoutOverrides),
    [baseConfig, _layoutOverrides],
  );
  const list = useDataList(config);

  const [createOpen, setCreateOpen] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState<ConfigSnapshot | null>(
    null,
  );

  useEffect(() => {
    dataListWorkflowRegistry.registerRowActionHandler(
      'claimflow-config-restore',
      (row) => {
        setRestoreTarget(row as unknown as ConfigSnapshot);
        return Promise.resolve();
      },
    );
  }, []);

  const handleRestoreApplied = useCallback(
    (summary: string) => {
      toast({
        variant: 'success',
        title: 'Restauration appliquée',
        message: summary,
      });
      setRestoreTarget(null);
    },
    [toast],
  );

  const handleCreated = useCallback(() => {
    toast({
      variant: 'success',
      title: 'Sauvegarde créée',
      message: 'La sauvegarde de la configuration a été enregistrée.',
    });
    setCreateOpen(false);
  }, [toast]);

  return (
    <div>
      <div className="flex items-center gap-2 bg-secondary-50 border-b border-secondary-200 px-4 py-3 rounded-t-lg">
        <Icon icon={Archive} size="md" color="primary" />
        <Text as="span" weight="semibold" size="base">
          {resolveCardTitle(BASE_TITLE, _layoutOverrides)}
        </Text>
        <Text as="span" size="sm" color="muted">
          ({list.totalItems})
        </Text>
        <div className="ml-auto">
          <Button
            variant="primary"
            size="sm"
            leftIcon={<Icon icon={Plus} size="xs" color="inherit" />}
            onClick={() => {
              setCreateOpen(true);
            }}
          >
            Créer une sauvegarde
          </Button>
        </div>
      </div>

      <DataList config={config} list={list}>
        <DataList.Table />
        <DataList.Footer />
      </DataList>

      <CreateSnapshotModal
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
        }}
        onCreated={handleCreated}
      />
      <RestoreSnapshotModal
        snapshot={restoreTarget}
        onClose={() => {
          setRestoreTarget(null);
        }}
        onApplied={handleRestoreApplied}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create snapshot modal (optional label)
// ---------------------------------------------------------------------------

function CreateSnapshotModal({
  open,
  onClose,
  onCreated,
}: Readonly<{ open: boolean; onClose: () => void; onCreated: () => void }>) {
  const [label, setLabel] = useState('');
  const create = useCreateSnapshot();
  const { reset } = create;

  // Reset in the close/success handlers (never in an effect) so the label and
  // mutation state are fresh on the next open.
  const handleClose = useCallback(() => {
    setLabel('');
    reset();
    onClose();
  }, [reset, onClose]);

  const handleConfirm = useCallback(() => {
    create.mutate(
      { label },
      {
        onSuccess: () => {
          setLabel('');
          onCreated();
        },
      },
    );
  }, [create, label, onCreated]);

  return (
    <Modal open={open} onClose={handleClose} size="sm">
      <Modal.Header title="Créer une sauvegarde" />
      <Modal.Body>
        <VStack gap={3}>
          <Text size="sm" color="muted">
            Capture l’état actuel de la configuration du locataire. Vous pourrez
            la restaurer ultérieurement.
          </Text>
          <TextField
            label="Libellé (facultatif)"
            placeholder="ex. Avant migration workflow"
            value={label}
            maxLength={200}
            onChange={(event) => {
              setLabel(event.target.value);
            }}
          />
          {create.isError && (
            <Alert variant="error" title="Échec de la création">
              {create.error instanceof Error
                ? create.error.message
                : 'La sauvegarde n’a pas pu être créée.'}
            </Alert>
          )}
        </VStack>
      </Modal.Body>
      <Modal.Footer>
        <HStack gap={2} justify="end">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={create.isPending}
          >
            Annuler
          </Button>
          <Button
            variant="primary"
            loading={create.isPending}
            onClick={handleConfirm}
          >
            Créer
          </Button>
        </HStack>
      </Modal.Footer>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Restore snapshot modal (dry-run preview → confirm)
// ---------------------------------------------------------------------------

function RestoreSnapshotModal({
  snapshot,
  onClose,
  onApplied,
}: Readonly<{
  snapshot: ConfigSnapshot | null;
  onClose: () => void;
  onApplied: (summary: string) => void;
}>) {
  const dryRun = useRestoreSnapshot();
  const apply = useRestoreSnapshot();

  const { mutate: mutateDryRun, reset: resetDryRun } = dryRun;
  const { reset: resetApply } = apply;

  useEffect(() => {
    if (!snapshot) {
      resetDryRun();
      resetApply();
      return;
    }
    mutateDryRun({ snapshotId: snapshot.id, dryRun: true });
  }, [snapshot, mutateDryRun, resetDryRun, resetApply]);

  const handleConfirm = useCallback(() => {
    if (!snapshot) return;
    apply.mutate(
      { snapshotId: snapshot.id, dryRun: false },
      {
        onSuccess: (report) => {
          onApplied(restoreReportSummary(report));
        },
      },
    );
  }, [apply, snapshot, onApplied]);

  return (
    <Modal open={snapshot !== null} onClose={onClose} size="lg">
      <Modal.Header title="Restaurer cette sauvegarde" />
      <Modal.Body>
        {snapshot && (
          <VStack gap={3}>
            <Text size="sm">
              La restauration applique la sauvegarde en mode fusion (aucune
              suppression). Aperçu :
            </Text>

            {dryRun.isPending && (
              <HStack gap={2} align="center">
                <Spinner size="sm" />
                <Text size="sm" color="muted">
                  Calcul de l’aperçu…
                </Text>
              </HStack>
            )}
            {dryRun.isError && (
              <Alert variant="error" title="Échec de l’aperçu">
                {dryRun.error instanceof Error
                  ? dryRun.error.message
                  : 'Impossible de calculer l’aperçu de la restauration.'}
              </Alert>
            )}
            {dryRun.data && <RestoreReportView report={dryRun.data} />}

            {apply.isError && (
              <Alert variant="error" title="Échec de la restauration">
                {apply.error instanceof Error
                  ? apply.error.message
                  : 'La restauration a échoué.'}
              </Alert>
            )}
          </VStack>
        )}
      </Modal.Body>
      <Modal.Footer>
        <HStack gap={2} justify="end">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={apply.isPending}
          >
            Annuler
          </Button>
          <Button
            variant="primary"
            loading={apply.isPending}
            disabled={dryRun.isPending || dryRun.isError}
            onClick={handleConfirm}
          >
            Confirmer la restauration
          </Button>
        </HStack>
      </Modal.Footer>
    </Modal>
  );
}
